import { DependentTypes } from '../../pages/account-info/features/dependents/dependents-model';
import { RegularExpressions } from '../constants/regular-expressions';
import { replaceAll } from './string-helpers';

function getDateStringWithoutTimezone(dateString: string): string {
    if (!dateString || (dateString?.startsWith('1900'))) {
        return null;
    }

    let copy = dateString;

    if (copy?.match(RegularExpressions.FailedJsDatePattern)) {
        copy = replaceAll(dateString, '-', '/');
    }
    const timezoneIndex = copy.indexOf('T00:');

    if (timezoneIndex > 0) {
        return copy.substring(0, timezoneIndex);
    }

    return copy;
}

export function adjustToNYTime(date: Date): Date {
    return new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
}

export function getCurrentTimeNY(): Date {
    return adjustToNYTime(new Date());
}

export function adjustForLocalOffset(date: Date): Date {
    const timeOffsetInMS: number = date.getTimezoneOffset() * 60000;
    date.setTime(date.getTime() + timeOffsetInMS);
    return date;
}

export function getFormattedDate(stringDate: string, useDashes?: boolean, skipOffSet?: boolean) {

    if (!stringDate) {
        return null;
    }

    let copy = stringDate;

    if (copy?.match && copy?.match(RegularExpressions.FailedJsDatePattern)) {
        copy = replaceAll(stringDate, '-', '/');
    }

    let date;
    if (skipOffSet)
        date = new Date(copy);
    else
        date = adjustForLocalOffset(new Date(copy));

    const year = date.getFullYear();

    let month = (1 + date.getMonth()).toString();
    month = month.length > 1 ? month : `0${month}`;

    let day = date.getDate().toString();
    day = day.length > 1 ? day : `0${day}`;
    const divider = useDashes ? '-' : '/';
    return `${month}${divider}${day}${divider}${year}`;
}

export function getFormattedDateYmd(stringDate: string, useDashes?: boolean) {

    if (!stringDate) {
        return null;
    }
    const date = adjustForLocalOffset(new Date(stringDate));

    const year = date.getFullYear();

    let month = (1 + date.getMonth()).toString();
    month = month.length > 1 ? month : `0${month}`;

    let day = date.getDate().toString();
    day = day.length > 1 ? day : `0${day}`;


    const divider = useDashes ? '-' : '/';
    return `${year}${divider}${month}${divider}${day}`;
}

export function getDateRange(startDate: string, endDate: string, presentDateText: string) {
    if (!startDate && !endDate) {
        return null;
    }

    return `${getFormattedDate(startDate)} - ${endDate ? getFormattedDate(endDate) : presentDateText}`;

}


export const datesAreOnSameDay = (first, second) =>
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate();


export function getNumberOfDays(start, end) {
    const date1 = new Date(start);
    const date2 = new Date(end);

    // Set the time portion to midnight (00:00:00) for both dates
    date1.setHours(0, 0, 0, 0);
    date2.setHours(0, 0, 0, 0);

    // One day in milliseconds
    const oneDay = 1000 * 60 * 60 * 24;

    // Calculating the time difference between two dates
    const diffInTime = date2.getTime() - date1.getTime();

    // Calculating the no. of days between two dates
    const diffInDays = Math.round(diffInTime / oneDay);

    return diffInDays;
}

export const firstDayOfNextCalendarYear = () => {

    return new Date(new Date().getFullYear() + 1, 0, 1);

};

export const GetTime = (timeStamp: string) => {
    const time = timeStamp?.split('.')[0]?.slice(8, 12);
    const hrArray = time?.slice(0, 2);
    const mininteArray = time?.slice(2, 4);
    const hr = Number(`${hrArray[0]}${hrArray[1]}`);

    if (Number.isNaN(hr)) {
        return null;
    }

    const hr12 = hr > 12 ? hr - 12 : hr;
    const formattedHr = hr12 < 10 ? `0${hr12}` : hr12;
    return `${formattedHr}:${mininteArray} ${hr > 12 ? 'pm' : 'am'}`;
};

export const ymdToMdyFormat = (dateString: string, divider = '-', currentDivider = '-') => {
    const subStrings = dateString?.split(currentDivider);

    return `${subStrings[1]}${divider}${subStrings[2]}${divider}${subStrings[0]}`;
};

export function isChildOlderThanPerson(dependentSelectedType: string, childDateOfBirth: string, currentPersonDateOfBirth: string, currentMaskedPersonDateOfBirth: string): boolean {

    if (dependentSelectedType !== DependentTypes.child || !childDateOfBirth) return false;

    if (currentMaskedPersonDateOfBirth) {
        const currentPersonDateOfBirthYear = Number(currentMaskedPersonDateOfBirth.slice(currentMaskedPersonDateOfBirth.length - 4));
        const childDateOfBirthYear = (new Date(childDateOfBirth)).getFullYear();

        return childDateOfBirthYear < currentPersonDateOfBirthYear;
    }

    return new Date(childDateOfBirth) < new Date(currentPersonDateOfBirth);
}

export const getLetterMonthDate = (dateStringRaw: string) => {
    const dateString = getDateStringWithoutTimezone(dateStringRaw);

    if (!dateString) {
        return null;
    }

    const date = adjustForLocalOffset(new Date(dateString));

    return date.toLocaleString('default', { month: 'long', day: '2-digit', year: 'numeric' });
};

export const getFullLetterMonthDateTime = (dateStringRaw: string, language: string = 'en') => {
    const dateString = getDateStringWithoutTimezone(dateStringRaw);

    if (!dateString) {
        return null;
    }

    const date = adjustForLocalOffset(new Date(dateString));

    const mediumTime = new Intl.DateTimeFormat(language, {
        month: 'long',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    });

    return mediumTime.format(date);
};

export function getTimeNewHireConfirmationTime() {
    const nyTime = getCurrentTimeNY();

    return `${nyTime.toLocaleDateString('en-us', { weekday: 'short' })}, ${nyTime.toLocaleString('en-us', { month: 'short', day: '2-digit', year: 'numeric' })} at ${nyTime.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true })}`;

}

export function getHourAndMinutesAmPm(dateTimeString: string) {
    try {
        if (!dateTimeString) {
            return null;
        }
        const time = new Date(dateTimeString);

        if (Number.isNaN(time.getTime())) {
            return null;
        }

        return time?.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
    } catch {
        return null;
    }
}

export function formatDateWithHourAndMinutes(date: Date): string {
    // Get the components of the date
    const month = date.getMonth() + 1; // Months are zero-based
    const day = date.getDate();
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';

    // Convert hours to 12-hour format
    hours %= 12;
    hours = hours || 12; // 0 should be converted to 12

    // Add leading zeroes if necessary
    const formattedMonth = month < 10 ? `0${month}` : month;
    const formattedDay = day < 10 ? `0${day}` : day;
    const formattedHours = hours < 10 ? `0${hours}` : hours;

    // Concatenate components into desired format
    const formattedDate = `${formattedMonth}/${formattedDay}/${year} ${formattedHours}:${minutes} ${ampm}`;

    return formattedDate;
}

export function getMaskedDate(stringDate: string, index: number) {
    if (!stringDate) {
        return null;
    }

    return `XX/XX/${stringDate?.split('/')[index]}`;
}